'use client';

import { useStreamHandler } from '@coolblue-development/next-chat/client';
import type { PostMessageResponse } from '@coolblue-development/next-chat/types';
import { AdviceCueState, Entrance } from '@coolblue-development/next-chatbot-ui/client';
import type { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import type { ReactNode } from 'react';

type Props = {
  accountName?: string;
  correlationId: string;
  endpointUrl: string;
  product: Product;
};

const AdviceCues = ({ accountName, correlationId, endpointUrl, product }: Props): ReactNode => {
  const { t } = useTranslation('webshop.productPage');

  const { state, sendUserMessage } = useStreamHandler<AdviceCueState>({
    conversationEndpointUrl: endpointUrl,
    initialState: {
      uiMessages: [],
      showError: false,
      followUpQuestions: (product.adviceCueAdvice?.questions ?? []).slice(0, 2)
    },
    correlationId,
    customMessageHandlers: {
      AdviceCueFollowupQuestions: (
        incomingMessage: PostMessageResponse,
        currentState: AdviceCueState
      ) => {
        if (incomingMessage.type === 'AdviceCueFollowupQuestions') {
          return {
            ...currentState,
            followUpQuestions: incomingMessage?.payload?.questions || []
          };
        }
        return currentState;
      }
    }
  });

  return (
    <Entrance
      title={
        accountName
          ? t('adviceCues.title.withName', {
              customerName: accountName,
              productType: product.productType.singularName
            })
          : t('adviceCues.title.withoutName', {
              productType: product.productType.singularName
            })
      }
      state={state}
      onSendMessage={message =>
        sendUserMessage(message, {
          chatSource: 'product_advice_cue_agent',
          clientContext: { productId: String(product.id) }
        })
      }
    />
  );
};

export default AdviceCues;
